import { useOutletContext } from 'react-router';
import { User } from '../../../types/user';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { useMutation } from '@tanstack/react-query';
import UserForm from './UserForm';
import { useSnackbar } from 'notistack';
import { UserTemplateSubPageContext } from './types';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const EditUserPage = () => {
  const { updateUser, resendEmailInvitation } = useIotsApi();
  const { isFetching, refetch, updateContextUser, user } =
    useOutletContext<UserTemplateSubPageContext>();

  const { t } = useLanguage();

  const updateUserMutation = useMutation({
    mutationFn: updateUser,
  });

  const resendEmailInvitationMutation = useMutation({
    mutationFn: resendEmailInvitation,
  });

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const isLoading =
    isFetching ||
    updateUserMutation.isPending ||
    resendEmailInvitationMutation.isPending;

  return (
    <UserForm
      onSubmit={(values, callback) => {
        const updatedUser = {
          id: user.id,
          email: user.email,
          isActive: user.isActive,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,
          operatorId: values.operatorId || undefined,
        } as User;

        updateUserMutation.mutate(updatedUser, {
          onSuccess: () => {
            enqueueSnackbar({
              variant: 'success',
              message: t('content.user-update-success'),
            });
            // A successful update will just update the item cached data
            updateContextUser(updatedUser);
            refetch();
            callback();
          },
          onError: error => {
            enqueueSnackbar({
              variant: 'error',
              message: getErrorMessage(error, t('content.user-update-error')),
            });
            updateUserMutation.reset();
          },
        });
      }}
      isLoading={isLoading}
      disabled={isLoading}
      user={user}
      isResendingInvitation={resendEmailInvitationMutation.isPending}
      onResendInvitationRequest={user => {
        resendEmailInvitationMutation.mutate(user, {
          onSuccess: () => {
            enqueueSnackbar({
              variant: 'success',
              message: t('content.user-resend-email-invitation-success'),
            });
          },
          onError: error => {
            enqueueSnackbar({
              variant: 'error',
              message: getErrorMessage(
                error,
                t('content.user-resend-email-invitation-error'),
              ),
            });
            resendEmailInvitationMutation.reset();
          },
        });
      }}
    />
  );
};

export default EditUserPage;
